/**
 * Custom Menu (rail) — hybrid-3c §4.
 *
 * A quiet light surface, full viewport height and flush to the edges, set off
 * from the canvas by its right-hand border. The two-tone lockup at the top is
 * the one place the product name appears in-app; teal is spent on the active
 * entry alone (a tint, a darker label and a 3px leading marker, no border, no
 * shadow). Hairlines group the entries; the profile plate anchors the foot. On
 * a phone the rail is react-admin's temporary drawer and closes on navigation.
 */

import React from 'react';
import { usePermissions, useSidebarState, useTranslate } from 'react-admin';
import { Box, ListItemIcon, ListItemText, MenuItem as MuiMenuItem, Tooltip, useMediaQuery } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { useLocation, useNavigate } from 'react-router-dom';
import { FULL_VIEWPORT_HEIGHT } from '@/lightTheme';
import { usePanelAccess } from '@/features/developers/access';
import { DEVELOPERS_ENTRY, MENU_ENTRIES, isEntryVisible, resolveLocation } from './menuStructure';
import { ProfileRailCard } from './ProfileRailCard';

interface StyledMenuItemProps {
  open: boolean;
}

const StyledMenuItem = styled(MuiMenuItem, {
  shouldForwardProp: (prop) => prop !== 'open',
})<StyledMenuItemProps>(({ theme, open }) => {
  const item = theme.custom.menu.item;
  return {
    position: 'relative',
    margin: item.margin,
    padding: item.padding,
    borderRadius: item.borderRadius,
    color: item.color(theme),
    justifyContent: open ? 'flex-start' : 'center',
    fontSize: item.fontSize,
    minHeight: item.minHeight,
    display: 'flex',
    gap: item.gap,
    transition: 'background-color 0.2s ease-in-out, color 0.2s ease-in-out',
    '& .MuiListItemText-root': { minWidth: 0, margin: 0 },
    '& .MuiListItemText-primary': {
      fontSize: item.fontSize,
      lineHeight: item.lineHeight,
      fontWeight: item.fontWeight,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    '& .MuiListItemIcon-root': {
      minWidth: 'auto',
      color: item.iconColor(theme),
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      '& svg': { fontSize: item.iconSize },
    },

    '&:hover:not(.active)': {
      color: item.hover.color(theme),
      backgroundColor: open ? item.hover.backgroundColor(theme) : 'transparent',
      ...(!open && {
        '& .MuiListItemIcon-root': { backgroundColor: item.hover.backgroundColor(theme) },
      }),
    },

    '&.active': {
      color: open ? item.active.color(theme) : item.closedActiveIcon.color(theme),
      backgroundColor: open ? item.active.backgroundColor(theme) : 'transparent',
      boxShadow: item.active.boxShadow,
      '& .MuiListItemText-primary': { fontWeight: item.active.fontWeight },

      // The leading marker is skipped entirely when its token is `transparent`,
      // rather than painted invisible.
      '&::before':
        open && item.active.marker(theme) !== 'transparent'
          ? {
              content: '""',
              position: 'absolute',
              left: 0,
              top: item.active.markerInset,
              bottom: item.active.markerInset,
              width: item.active.markerWidth,
              borderRadius: item.active.markerRadius,
              backgroundColor: item.active.marker(theme),
            }
          : undefined,

      '& .MuiListItemIcon-root': {
        color: open ? item.active.iconColor(theme) : item.closedActiveIcon.color(theme),
        backgroundColor: open ? 'transparent' : item.closedActiveIcon.backgroundColor(theme),
        padding: open ? 0 : '6px',
        borderRadius: open ? 0 : item.closedActiveIcon.borderRadius,
      },

      '&:hover': {
        backgroundColor: open ? item.active.hover.backgroundColor(theme) : 'transparent',
      },
    },
  };
});

export const CustomMenu: React.FC = () => {
  const translate = useTranslate();
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [isSidebarOpen, setSidebarOpen] = useSidebarState();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { permissions: role } = usePermissions<string>();
  const access = usePanelAccess();

  const expanded = isSidebarOpen || isSmallScreen;
  // A documentation-only user gets a one-entry rail; a user with no access, an empty one.
  const visibleEntries = MENU_ENTRIES.filter((entry) =>
    access === 'panel' ? isEntryVisible(entry, role) : access === 'documentation' && entry.name === DEVELOPERS_ENTRY,
  );
  const activeEntry = resolveLocation(location.pathname, location.search, role)?.entry;
  const width = isSmallScreen ? 'auto' : isSidebarOpen ? theme.custom.menu.openWidth : theme.custom.menu.closedWidth;

  const handleMenuClick = (to: string): void => {
    navigate(to);
    if (isSmallScreen) setSidebarOpen(false);
  };

  return (
    <Box
      component="nav"
      aria-label={translate('shell.rail.label')}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        ...FULL_VIEWPORT_HEIGHT,
        boxSizing: 'border-box',
        backgroundColor: theme.palette.background.menu,
        borderRight: isSmallScreen ? 'none' : `1px solid ${theme.custom.menu.border}`,
        padding: isSmallScreen ? '12px' : isSidebarOpen ? '16px 10px' : '16px 6px',
        width: isSmallScreen ? theme.custom.menu.openWidth : width,
        minWidth: width,
        maxWidth: isSmallScreen ? '100%' : width,
        transition:
          'width 0.2s ease-in-out, min-width 0.2s ease-in-out, max-width 0.2s ease-in-out, padding 0.2s ease-in-out',
        overflow: 'hidden',
      }}
    >
      {expanded && (
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '6px', padding: '4px 8px 20px' }}>
          <Box
            component="span"
            sx={{ fontSize: '1.15rem', fontWeight: 700, letterSpacing: '-0.01em', color: theme.custom.menu.brand.color }}
          >
            Roommatik
          </Box>
          <Box component="span" sx={{ fontSize: '1.15rem', fontWeight: 400, color: theme.custom.menu.brand.mutedColor }}>
            KYC
          </Box>
        </Box>
      )}

      <Box
        sx={{
          flex: 1,
          overflowY: expanded ? 'auto' : 'hidden',
          overflowX: 'hidden',
          // Thin, idle-hidden scrollbar; the 6px gutter stays reserved so
          // revealing it shifts nothing.
          '&::-webkit-scrollbar': { width: '6px' },
          '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'transparent',
            borderRadius: '999px',
            transition: 'background-color 0.2s ease-in-out',
          },
          '&:hover::-webkit-scrollbar-thumb': { backgroundColor: theme.custom.menu.scrollbarThumb },
          '&:hover::-webkit-scrollbar-thumb:hover': { backgroundColor: theme.custom.menu.scrollbarThumbHover },
        }}
      >
        {visibleEntries.map((entry, index) => {
          const Icon = entry.icon;
          const active = activeEntry?.name === entry.name;
          const label = translate(entry.nameKey, { smart_count: 2, _: entry.name });
          return (
            <React.Fragment key={entry.name}>
              {entry.separatorBefore && index > 0 && (
                <Box
                  role="separator"
                  sx={{
                    height: '1px',
                    backgroundColor: theme.custom.menu.separatorColor,
                    margin: expanded ? '10px 8px' : '10px 6px',
                  }}
                />
              )}
              {/* Collapsed, the icon is all there is: the label moves to a tooltip. */}
              <Tooltip title={expanded ? '' : label} placement="right">
                <StyledMenuItem
                  onClick={() => handleMenuClick(entry.to)}
                  open={expanded}
                  className={active ? 'active' : ''}
                  aria-current={active ? 'page' : undefined}
                  aria-label={expanded ? undefined : label}
                >
                  <ListItemIcon>
                    <Icon />
                  </ListItemIcon>
                  {expanded && <ListItemText primary={label} primaryTypographyProps={{ noWrap: true }} />}
                </StyledMenuItem>
              </Tooltip>
            </React.Fragment>
          );
        })}
      </Box>

      <ProfileRailCard expanded={expanded} />
    </Box>
  );
};

export default CustomMenu;
